import { CRM_WEB_PAGES_URLS } from '../CRM_WEB_PAGES_URLS';
import { ADMIN_WEB_PAGES_URLS } from '../EMR_WEB_PAGES_URLS';
import { PATIENT_WEB_PAGES_URLS } from '../PATIENT_WEB_PAGES_URLS';
import { usStates } from '../data';
import { OrganizationSettings } from '@care-portals/api-interfaces';

/**
 *
 * @param orgKey The Prefix to .portals.care where their portal will live
 * @param orgName String that describes brand name
 * @param orgDomain Organization main website
 * @returns
 */
export const defaultOrgConfig: (
  orgKey: string,
  orgName: string,
  orgDomain: string
) => OrganizationSettings = (orgKey, orgName, orgDomain) => ({
  name: `${orgName}`,
  ecommerceEnabled: false,
  // if true, remove nmi billing page from patient web app
  stripeEnabled: false,
  patientPortalDomain: `${orgKey}.portals.care`,
  customerServiceEmail: `help@${orgDomain}`,
  currency: 'usd',
  appointmentconsentSection: `
    <p style="text-align: center;color: #536476;">
    By clicking on {{cta_label}} you agree to our:<br />
    <a
      href="javascript:window.open('https://${orgDomain}/privacy-policy', 'popup', 'width=600,height=600');"
    >
        Privacy Policies
    </a>
    <br>
    <a
      href="javascript:window.open('https://${orgDomain}.com/terms-conditions', 'popup', 'width=600,height=600');"
    >
    Terms & Conditions
    </a>
      </p>
    `,

  // navigation
  crmWebAppLayout: {
    title: orgName,
    icon: '',
    locations: [...usStates.map(s => ({ label: s.province, value: s.code }))],
    mainMenu: [
      {
        title: 'Leads',
        link: CRM_WEB_PAGES_URLS.leads.list,
        icon: 'fa-regular fa-address-book',
        roles: ['*']
      },
      {
        title: 'Appointments',
        link: CRM_WEB_PAGES_URLS.appointments.calendar,
        icon: 'fa-regular fa-calendar-check',
        roles: ['*']
      },
      {
        title: 'Tasks',
        link: CRM_WEB_PAGES_URLS.tasks.list,
        icon: 'fa-solid fa-list-check',
        roles: ['*']
      },
      {
        title: 'Segments',
        link: CRM_WEB_PAGES_URLS.segment.list,
        icon: 'fa-solid fa-table-columns',
        roles: ['*']
      },
      {
        title: 'Store Manager',
        link: '',
        icon: 'fas fa-shop',
        roles: ['admin', 'developer', 'owner', 'marketing'],
        items: [
          {
            title: 'Products',
            link: CRM_WEB_PAGES_URLS.products.list,
            icon: 'fas fa-product'
          },
          {
            title: 'Categories',
            link: CRM_WEB_PAGES_URLS.productCategories.list,
            icon: 'fas fa-list'
          },
          {
            title: 'Coupons',
            link: CRM_WEB_PAGES_URLS.coupons.list,
            icon: 'fas fa-gift'
          },
          {
            title: 'Promotions',
            link: CRM_WEB_PAGES_URLS.promotions.list,
            icon: 'fas fa-gift'
          }
        ]
      },
      {
        title: 'Admin',
        link: '',
        icon: 'fas fa-user-cog',
        roles: ['admin', 'developer', 'owner'],
        items: [
          {
            title: 'Services',
            link: CRM_WEB_PAGES_URLS.services.list,
            icon: 'fas fa-medkit'
          },
          {
            title: 'Schedules',
            link: CRM_WEB_PAGES_URLS.schedules.list,
            icon: 'fas fa-calendar'
          },
          {
            title: 'Forms',
            link: CRM_WEB_PAGES_URLS.forms.list,
            icon: 'fas fa-table'
          },
          {
            title: 'Workflows',
            link: CRM_WEB_PAGES_URLS.workflows.list,
            icon: 'fas fa-code-branch'
          },
          {
            title: 'Templates',
            link: CRM_WEB_PAGES_URLS.emailTemplates.list,
            icon: 'fas fa-palette'
          },
          {
            title: 'Users',
            link: CRM_WEB_PAGES_URLS.users.list,
            icon: 'fas fa-lock'
          },
          {
            title: 'Settings',
            link: CRM_WEB_PAGES_URLS.settings.base,
            icon: 'fa-solid fa-sliders'
          }
        ]
      }
    ]
  },
  emrWebAppLayout: {
    title: orgName,
    defaultPingMsg: `🛎️ Ping! ${orgName} team is waiting for your response. To respond, please go to https://${orgKey}.portals.care/messages`,
    questionnaires: [
      'facesheet',
      'facesheet_ext',
      'selfie',
      'govId',
      'bodySelfie',
      'wlus',
      'wlusmonthly'
    ],
    mainMenu: [
      {
        title: 'Patients',
        name: 'patients',
        link: ADMIN_WEB_PAGES_URLS.customers.allList,
        icon: 'fas fa-user-injured',
        roles: ['*']
      },
      {
        title: 'Consultations',
        name: 'consultations',
        link: ADMIN_WEB_PAGES_URLS.consultations.onlineList,
        icon: 'fas fa-user-md',
        roles: ['*']
      },
      {
        title: 'Orders',
        name: 'orders',
        link: ADMIN_WEB_PAGES_URLS.orders.allList,
        icon: 'fas fa-shopping-cart',
        roles: ['*']
      },
      {
        title: 'Subscriptions',
        name: 'subscriptions',
        link: ADMIN_WEB_PAGES_URLS.subscriptions.allList,
        icon: 'fas fa-bars',
        roles: ['*']
      },
      {
        title: 'Drugs',
        link: ADMIN_WEB_PAGES_URLS.drugs.allList,
        icon: 'fas fa-pills',
        roles: ['*']
      },
      {
        title: 'RXs',
        name: 'rxs',
        link: ADMIN_WEB_PAGES_URLS.prescriptions.allList,
        icon: 'fas fa-prescription',
        roles: ['pharmacy', 'admin', 'owner']
      }
    ]
  },
  patientWebAppLayout: {
    title: orgName,
    brand: orgKey,
    shopUrl: `https://${orgDomain}`,
    colors: {
      primaryColor: '#ff8573',
      secondaryColor: '#0b3bd9'
    },
    gtm: {
      id: ''
      //   url: `https://gtm.${orgDomain}/gtm.js`
    },
    topAction: {
      title: 'Shop',
      link: `https://${orgDomain}`
    },
    logo: {
      img: `https://${orgKey}.com/wp-content/uploads/2024/07/Logo200x60.jpg`,
      link: '/home'
    },
    referrals: {
      enabled: false,
      referrerAmount: 50,
      refereeAmount: 30,
      referralURI: `https://${orgKey}.portals.care/auth/signup`,
      referralFAQ: `https://${orgDomain}/referral-rewards/`
    },
    privacyPolicyUrl: `https://${orgDomain}/privacy-policy`,
    termsOfUseUrl: `https://${orgDomain}/terms-of-use`,
    nmiPublicKey: 'KMAc49-77793P-Jg2CM3-a47395',
    stripePublicKey:
      'pk_test_51JcjAfKeCoUxIkZxqifwsMKca7GgkWZHB9cQoJC5zrRbk7bxtFCpC1jEkK37r8Fmk8W9xhy7RsjtchX03z1xZoOK00EgXYoj08',
    availableStates: {
      us: [...usStates.filter(s => ![].includes(s.code))]
    },
    mainMenu: [
      {
        title: 'Profile',
        link: PATIENT_WEB_PAGES_URLS.account.profile,
        svg: 'assets/icons/profile.svg',
        roles: ['*']
      },
      {
        title: 'Orders',
        link: PATIENT_WEB_PAGES_URLS.account.orders,
        svg: 'assets/icons/orders.svg',
        roles: ['*']
      },
      {
        title: 'Consultations',
        link: PATIENT_WEB_PAGES_URLS.account.consultations,
        svg: 'assets/icons/consultations.svg',
        roles: ['*']
      },
      {
        title: 'Prescriptions',
        link: PATIENT_WEB_PAGES_URLS.account.prescriptions,
        svg: 'assets/icons/rxs.svg',
        roles: ['*']
      },
      {
        title: 'Medical Inbox',
        link: PATIENT_WEB_PAGES_URLS.messages.inbox,
        svg: 'assets/icons/medical-chat.svg',
        roles: ['*']
      },
      {
        title: 'Billing',
        link: PATIENT_WEB_PAGES_URLS.account.billing,
        svg: 'assets/icons/billing.svg',
        roles: ['*']
      },
      {
        title: 'Shop',
        link: 'shop',
        svg: 'assets/icons/shop.svg',
        type: 'external',
        roles: ['*']
      }
    ]
  },
  pharmacies: {
    Foothills: {
      _id: '66ab2728d1f4e892b4d206dc',
      belugaId: '1978',
      states: ['IN', 'MN', 'NE', 'TX', 'NC']
    },
    Promise: {
      _id: '66ab275ad1f4e892b4d206e1',
      belugaId: '8453',
      states: [
        'AK',
        'AZ',
        'CO',
        'CT',
        'DE',
        'FL',
        'GA',
        'HI',
        'ID',
        'IL',
        'IA',
        'KS',
        'KY',
        'ME',
        'MD',
        'MA',
        'MI',
        'MO',
        'MT',
        'NV',
        'NH',
        'NJ',
        'NM',
        'NY',
        'NC',
        'ND',
        'OH',
        'OK',
        'OR',
        'PA',
        'RI',
        'SD',
        'TN',
        'UT',
        'VT',
        'VA',
        'WA',
        'WV',
        'WI',
        'WY'
      ]
    },
    pharmacyHub: {
      _id: '673fa5f9594a65ea5422b79c',
      belugaId: '271328',
      states: [
        'AL',
        'AK',
        'AZ',
        'AR',
        'CA',
        'CO',
        'CT',
        'DE',
        'FL',
        'GA',
        'HI',
        'ID',
        'IL',
        'IN',
        'IA',
        'KS',
        'KY',
        'LA',
        'ME',
        'MD',
        'MA',
        'MI',
        'MN',
        'MS',
        'MO',
        'MT',
        'NE',
        'NV',
        'NH',
        'NJ',
        'NM',
        'NY',
        'NC',
        'ND',
        'OH',
        'OK',
        'OR',
        'PA',
        'RI',
        'SC',
        'SD',
        'TN',
        'TX',
        'UT',
        'VT',
        'VA',
        'WA',
        'WV',
        'WI',
        'WY'
      ]
    }
  }
});
